/**
 * POST waitlist signup. Uses REACT_APP_WAITLIST_URL if set, else REACT_APP_BACKEND_URL.
 * Both may be origin-only (https://svc.onrender.com) or include paths — we always POST to
 * `{origin}/api/waitlist` so misconfigured env cannot drop the path.
 * If neither is set, resolves successfully after a short delay (local / static teaser).
 *
 * Render free tier spins services down when idle; call `warmupWaitlistBackend()` on landing
 * mount and/or rely on `.github/workflows/render-waitlist-keepalive.yml` to reduce cold starts.
 */
function resolveWaitlistApiOrigin() {
  const explicit = (process.env.REACT_APP_WAITLIST_URL || "").trim();
  const backend = (process.env.REACT_APP_BACKEND_URL || "").trim();
  const raw = explicit || backend;
  if (!raw) return "";
  try {
    return new URL(raw).origin;
  } catch {
    return "";
  }
}

function resolveWaitlistPostUrl() {
  const origin = resolveWaitlistApiOrigin();
  return origin ? `${origin}/api/waitlist` : "";
}

function resolveWaitlistHealthUrl() {
  const origin = resolveWaitlistApiOrigin();
  return origin ? `${origin}/health` : "";
}

let lastWarmupAt = 0;

/**
 * GET /health on the waitlist API origin (no-op if env unset or SSR).
 * Helps wake a cold Render free instance before the user submits; safe to ignore failures.
 *
 * @param {{ force?: boolean }} [options] — force=true skips dedupe window
 */
export async function warmupWaitlistBackend(options = {}) {
  const { force = false } = options;
  const healthUrl = resolveWaitlistHealthUrl();
  if (!healthUrl || typeof window === "undefined") return;

  const ttlMs = 45_000;
  if (!force && Date.now() - lastWarmupAt < ttlMs) return;

  try {
    await fetch(healthUrl, {
      method: "GET",
      cache: "no-store",
      signal: AbortSignal.timeout(90_000),
    });
  } catch {
    // Cold start can exceed timeout; still worth trying again later.
  }
  lastWarmupAt = Date.now();
}

export async function submitWaitlist(email) {
  const trimmed = String(email ?? "").trim();
  const url = resolveWaitlistPostUrl();

  if (!url) {
    await new Promise((r) => setTimeout(r, 450));
    return { ok: true, mock: true, duplicate: false };
  }

  if (typeof window !== "undefined") {
    try {
      const target = new URL(url, window.location.href);
      if (target.origin === window.location.origin) {
        throw new Error(
          "Waitlist API URL points to this site (404 expected). In Vercel set REACT_APP_BACKEND_URL to your Render host only, e.g. https://your-service.onrender.com — then redeploy a fresh production build.",
        );
      }
    } catch (e) {
      if (e instanceof TypeError) {
        throw new Error("Invalid waitlist API URL. Check REACT_APP_WAITLIST_URL / REACT_APP_BACKEND_URL.");
      }
      throw e;
    }
  }

  await warmupWaitlistBackend({ force: true });

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email: trimmed }),
  });

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    let detail =
      (body && (body.detail || body.message)) ||
      (typeof body === "string" ? body : "") ||
      res.statusText ||
      "Request failed";
    if (Array.isArray(detail)) {
      detail = detail.map((d) => d.msg || d).join(", ");
    } else {
      detail = String(detail);
    }
    if (res.status === 404) {
      detail = `${detail} — POST ${url}. For Render waitlist, URL must end with /api/waitlist (redeploy after fixing Vercel env).`;
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }

  return body || { ok: true };
}
